/** SUNTRONIX ONE — Global Product Colour Intelligence.
 *  Catalogue Data Quality → Colour Enrichment. Detects an accurate Colour /
 *  Finish for every product (name → description → ONE AI), never guessing and
 *  never recolouring an image. Official marketing names preserved.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Palette, Loader2, PlayCircle, RefreshCw, Gauge, Wand2, Pencil } from "lucide-react";

const TONE = {
  VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  DETECTED: "bg-sky-100 text-sky-800 border-sky-200",
  REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  MISSING: "bg-rose-100 text-rose-700 border-rose-200",
  IMAGE_COLOUR_MISMATCH: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  N_A: "bg-slate-100 text-slate-500 border-slate-200",
};
const FILTERS = ["", "MISSING", "REVIEW", "DETECTED", "N_A"];

function Cov({ label, value, tone = "text-slate-900", icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className={"text-[19px] font-bold mt-0.5 tabular-nums " + tone}>{value ?? "—"}</div>
    </div>
  );
}

export default function ColourEnrichment() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("MISSING");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const poll = useRef(null);

  const loadOverview = useCallback(() => api.get("/colour-enrichment/overview").then(({ data }) => setOv(data)).catch(() => {}), []);
  const loadRows = useCallback(() => {
    setLoading(true);
    api.get("/colour-enrichment/products", { params: { status: status || undefined, q: q || undefined, page_size: 50 } })
      .then(({ data }) => setRows(data.items || []))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, [status, q]);

  useEffect(() => { loadOverview(); api.get("/colour-enrichment/scan/job").then(({ data }) => setJob(data.job)).catch(() => {}); }, [loadOverview]);
  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    if (job?.status === "RUNNING") {
      poll.current = setInterval(async () => {
        try {
          const { data } = await api.get("/colour-enrichment/scan/job");
          setJob(data.job);
          if (data.job?.status !== "RUNNING") { clearInterval(poll.current); loadOverview(); loadRows(); }
        } catch { /* ignore */ }
      }, 2000);
      return () => clearInterval(poll.current);
    }
  }, [job?.status, loadOverview, loadRows]);

  const startScan = async () => {
    try {
      const { data } = await api.post("/colour-enrichment/scan");
      if (data.already_running) toast.info("A scan is already running");
      else toast.success("Product colour scan started");
      const { data: j } = await api.get("/colour-enrichment/scan/job");
      setJob(j.job);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start scan"); }
  };

  const resolve = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/colour-enrichment/${p.id}/resolve`);
      if (data.outcome === "DETECTED") toast.success(`Detected ${data.colour}`);
      else if (data.outcome === "REVIEW") toast.info(`AI candidate: ${data.candidate?.colour} — confirm before use`);
      else if (data.outcome === "N_A") toast.message("Colour not applicable");
      else toast.message(data.message || "No colour evidence — never guessed");
      loadOverview(); loadRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(null); }
  };

  const setManual = async (p) => {
    const val = window.prompt(`Enter the official Colour / Finish for:\n${p.product_name}`, p.colour_candidate?.colour || p.colour || "");
    if (!val) return;
    const reason = window.prompt("Reason / source (audited):", "Verified from OEM source");
    if (reason === null) return;
    setBusy(p.id);
    try {
      await api.post(`/colour-enrichment/${p.id}/set`, { colour: val, reason });
      toast.success("Colour set & verified — image refreshed");
      loadOverview(); loadRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set"); }
    finally { setBusy(null); }
  };

  const running = job?.status === "RUNNING";

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="colour-enrichment-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><Palette className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Product Colour Intelligence</h1>
          <p className="text-[13px] text-slate-500">Accurate Colour / Finish on every product — detected from evidence, never guessed, never AI-recoloured. Official names preserved (Space Black stays Space Black).</p>
        </div>
      </div>

      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="colour-overview">
          <Cov label="Coverage" value={`${ov.coverage_pct}%`} tone="text-emerald-700" icon={Gauge} testid="col-cov-pct" />
          <Cov label="Verified" value={ov.verified} tone="text-emerald-700" testid="col-cov-verified" />
          <Cov label="Detected" value={ov.detected} tone="text-sky-700" testid="col-cov-detected" />
          <Cov label="Missing" value={ov.missing} tone="text-rose-700" testid="col-cov-missing" />
          <Cov label="Not applicable" value={ov.not_applicable} tone="text-slate-500" testid="col-cov-na" />
          <Cov label="Image mismatch" value={ov.image_colour_mismatch} tone="text-fuchsia-700" testid="col-cov-mismatch" />
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="colour-controls">
        <span className="text-[13px] text-slate-600">{ov?.needs_attention ?? 0} product(s) need attention</span>
        <div className="flex-1" />
        <Button disabled={running} onClick={startScan} data-testid="colour-scan-run" className="bg-slate-900 hover:bg-slate-800 text-white">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}
          Scan &amp; detect all
        </Button>
        <Button variant="outline" onClick={() => { loadOverview(); loadRows(); }} data-testid="colour-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {job && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="colour-job">
          <div className="flex items-center justify-between text-sm">
            <span className="font-mono font-semibold">{job.id}
              <span className={"ml-2 text-[11px] font-semibold " + (running ? "text-amber-600" : job.status === "DONE" ? "text-emerald-600" : "text-rose-600")} data-testid="colour-job-status">
                {running ? "SCAN RUNNING" : job.status}
              </span>
            </span>
            <span className="text-slate-500 text-xs" data-testid="colour-job-counts">{job.processed || 0} / {job.total || 0}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={"h-full transition-all " + (running ? "bg-sky-500" : "bg-emerald-500")}
              style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <Input data-testid="colour-search" value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]" placeholder="Search name, brand, model, colour…" />
        {FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`colour-filter-${s || "all"}`}
            className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
              (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {s ? s.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="colour-table">
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Product", "Brand", "Colour / Finish", "Status", "Reason", ""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No products in this state 🎉</td></tr>}
            {!loading && rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`colour-row-${p.id}`}>
                <td className="px-3 py-2 text-slate-800 max-w-[260px] truncate" title={p.product_name}>{p.product_name}</td>
                <td className="px-3 py-2 text-slate-500">{p.brand || "—"}</td>
                <td className="px-3 py-2 font-medium" data-testid={`colour-val-${p.id}`}>
                  {p.colour ? <span className="text-slate-900">{p.colour}</span> : <span className="text-rose-500">—</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge className={"text-[10px] border " + (TONE[p.colour_status] || TONE.MISSING)}>
                    {(p.colour_status || "MISSING").replace(/_/g, " ")}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-slate-500 max-w-[240px] truncate" title={p.colour_reason}>{p.colour_reason || "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin inline text-slate-400" /> : (
                    <div className="flex items-center gap-1 justify-end">
                      {["MISSING", "REVIEW", "IMAGE_COLOUR_MISMATCH"].includes(p.colour_status) && (
                        <button onClick={() => resolve(p)} title="Detect colour (evidence-based / AI candidate)"
                          data-testid={`colour-resolve-${p.id}`} className="p-1.5 rounded hover:bg-indigo-50 text-indigo-500">
                          <Wand2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => setManual(p)} title="Set colour manually (audited, refreshes image)"
                        data-testid={`colour-set-${p.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
